import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  Spinner,
  Text,
  makeStyles,
  tokens,
  Button,
  Input,
  Dropdown,
  Option,
  Tooltip,
  MessageBar,
  MessageBarBody,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  DialogTrigger,
} from '@fluentui/react-components';
import {
  Eye20Regular,
  Search20Regular,
  Dismiss20Regular,
  Class20Regular,
  Checkmark20Regular,
  People20Regular,
  PersonBoard20Regular,
  Play20Regular,
  Archive20Regular,
  Subtract20Regular,
  ArrowCounterclockwise20Regular,
} from '@fluentui/react-icons';
import type { EducationClass, EducationClassSummary, PagedResponse } from '../../types';
import educationService from '../../services/educationService';
import ExportButton from '../Common/ExportButton';
import ClassDetails from './ClassDetails';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 200px)',
  },
  searchArea: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  searchDropdown: {
    minWidth: '150px',
  },
  searchInput: {
    flex: 1,
    minWidth: '300px',
    maxWidth: '500px',
  },
  totalText: {
    marginLeft: 'auto',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  table: {
    backgroundColor: tokens.colorNeutralBackground1,
    width: '100%',
    tableLayout: 'fixed',
  },
  tableHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  headerCell: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  tableRow: {
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  cellName: {
    width: '35%',
  },
  cellExternalId: {
    width: '25%',
  },
  cellActions: {
    width: '5%',
    textAlign: 'center',
  },
  cellCount: {
    width: '10%',
    textAlign: 'center',
  },
  cellTeams: {
    width: '10%',
    textAlign: 'center',
  },
  countContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  centeredCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  teamsIcon: {
    color: tokens.colorPaletteGreenForeground1,
  },
  teamsIconNone: {
    color: tokens.colorNeutralForeground4,
  },
  teamsIconActivate: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    '&:hover': {
      color: tokens.colorBrandForeground2,
    },
  },
  archivedIcon: {
    color: tokens.colorPaletteRedForeground1,
  },
  archivedIconNotArchived: {
    color: tokens.colorNeutralForeground3,
  },
  archivedPopoverSurface: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '8px 12px',
    alignItems: 'flex-start',
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  classIcon: {
    color: tokens.colorPaletteBlueForeground2,
    marginRight: '8px',
    verticalAlign: 'middle',
  },
  nameContent: {
    display: 'flex',
    alignItems: 'center',
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  paginationInfo: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  paginationButtons: {
    display: 'flex',
    gap: '8px',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
  },
  loadingMore: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px',
    gap: '8px',
  },
  warningBar: {
    marginBottom: '12px',
  },
  emptyState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '48px',
    color: tokens.colorNeutralForeground3,
  },
});

type SearchBy = 'name' | 'classCode' | 'id';

const PAGE_SIZE = 25;

export const ClassesList: React.FC = () => {
  const styles = useStyles();

  // Data state
  const [classes, setClasses] = useState<EducationClass[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Pagination state
  const [nextLink, setNextLink] = useState<string | null>(null);

  // Search state
  const [searchBy, setSearchBy] = useState<SearchBy>('classCode');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Summary state (members, teachers, teams)
  const [classSummaries, setClassSummaries] = useState<Map<string, EducationClassSummary>>(new Map());
  const [loadingSummaries, setLoadingSummaries] = useState<Set<string>>(new Set());

  // State for Teams activation
  const [activatingTeams, setActivatingTeams] = useState<Set<string>>(new Set());

  // State for Teams unarchive
  const [unarchivingTeams, setUnarchivingTeams] = useState<Set<string>>(new Set());
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
  const [unarchiveTarget, setUnarchiveTarget] = useState<{ classId: string; className: string } | null>(null);

  // State for Teams archive
  const [archivingTeams, setArchivingTeams] = useState<Set<string>>(new Set());
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<{ classId: string; className: string } | null>(null);

  // State for archived column hover popover
  const [hoveredArchivedCell, setHoveredArchivedCell] = useState<string | null>(null);

  const loadClasses = async (isNewSearch: boolean = true) => {
    if (!searchTerm.trim()) {
      setError('Por favor, insira um termo de busca');
      return;
    }

    try {
      if (isNewSearch) {
        setLoading(true);
        setClasses([]);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      setHasSearched(true);

      let response: PagedResponse<EducationClass>;

      if (isNewSearch) {
        // First page - use regular getClasses
        response = await educationService.getClasses(1, PAGE_SIZE, searchTerm, searchBy);
        setClasses(response.data);
      } else if (nextLink) {
        // Subsequent pages - use nextLink endpoint
        response = await educationService.getClassesWithNextLink(nextLink);
        setClasses(prev => [...prev, ...response.data]);
      } else {
        return;
      }

      setNextLink(response.nextLink || null);
    } catch (err: any) {
      console.error('Error loading classes:', err);
      setError(err.message || 'Erro ao carregar turmas');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleSearch = () => {
    loadClasses(true);
  };

  const handleLoadMore = () => {
    loadClasses(false);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchBy('classCode');
    setClasses([]);
    setHasSearched(false);
    setError(null);
    setNextLink(null);
    setClassSummaries(new Map());
    setLoadingSummaries(new Set());
  };

  const handleViewClass = (classId: string) => {
    setSelectedClassId(classId);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedClassId(null);
  };

  const handleActivateTeam = async (classId: string, className: string) => {
    const confirmed = window.confirm(
      `Deseja ativar o Microsoft Teams para a turma "${className}"?`
    );
    if (!confirmed) return;

    setActivatingTeams(prev => new Set([...prev, classId]));

    try {
      const result = await educationService.activateTeam(classId);

      if (result.success) {
        setClassSummaries(prev => {
          const newMap = new Map(prev);
          const existing = newMap.get(classId);
          if (existing) {
            newMap.set(classId, { ...existing, hasTeam: true, teamStatus: 'active' });
          }
          return newMap;
        });
        alert(result.alreadyActivated
          ? 'O Teams ja estava ativado para esta turma.'
          : 'Teams ativado com sucesso!');
      } else {
        alert(`Erro ao ativar Teams: ${result.message || 'Erro desconhecido'}`);
      }
    } catch (err: any) {
      console.error('Error activating team:', err);
      alert(`Erro ao ativar Teams: ${err.message || 'Erro de conexao'}`);
    } finally {
      setActivatingTeams(prev => {
        const newSet = new Set(prev);
        newSet.delete(classId);
        return newSet;
      });
    }
  };

  const handleUnarchiveTeamConfirm = async () => {
    if (!unarchiveTarget) return;
    const { classId } = unarchiveTarget;

    setUnarchiveDialogOpen(false);
    setUnarchivingTeams(prev => new Set([...prev, classId]));

    try {
      const result = await educationService.unarchiveTeam(classId);

      if (result.success) {
        setClassSummaries(prev => {
          const newMap = new Map(prev);
          const existing = newMap.get(classId);
          if (existing) {
            newMap.set(classId, { ...existing, teamStatus: 'active' });
          }
          return newMap;
        });
      } else {
        alert(`Erro ao desarquivar Teams: ${result.message || 'Erro desconhecido'}`);
      }
    } catch (err: any) {
      console.error('Error unarchiving team:', err);
      const backendMessage = err.response?.data?.message;
      alert(`Erro ao desarquivar Teams: ${backendMessage || err.message || 'Erro de conexao'}`);
    } finally {
      setUnarchivingTeams(prev => {
        const newSet = new Set(prev);
        newSet.delete(classId);
        return newSet;
      });
      setUnarchiveTarget(null);
    }
  };

  const handleArchiveTeamConfirm = async () => {
    if (!archiveTarget) return;
    const { classId } = archiveTarget;

    setArchiveDialogOpen(false);
    setArchivingTeams(prev => new Set([...prev, classId]));

    try {
      const result = await educationService.archiveTeam(classId);

      if (result.success) {
        setClassSummaries(prev => {
          const newMap = new Map(prev);
          const existing = newMap.get(classId);
          if (existing) {
            newMap.set(classId, { ...existing, teamStatus: 'archived' });
          }
          return newMap;
        });
      } else {
        alert(`Erro ao arquivar Teams: ${result.message || 'Erro desconhecido'}`);
      }
    } catch (err: any) {
      console.error('Error archiving team:', err);
      const backendMessage = err.response?.data?.message;
      alert(`Erro ao arquivar Teams: ${backendMessage || err.message || 'Erro de conexao'}`);
    } finally {
      setArchivingTeams(prev => {
        const newSet = new Set(prev);
        newSet.delete(classId);
        return newSet;
      });
      setArchiveTarget(null);
    }
  };

  // Load class summaries (members, teachers, teams)
  const loadClassSummaries = useCallback(async (classIds: string[]) => {
    // Only load for classes not already loaded or loading
    const idsToLoad = classIds.filter(id =>
      !classSummaries.has(id) && !loadingSummaries.has(id)
    );

    if (idsToLoad.length === 0) return;

    // Mark as loading
    setLoadingSummaries(prev => new Set([...prev, ...idsToLoad]));

    try {
      const summaries = await educationService.getClassesSummary(idsToLoad);

      setClassSummaries(prev => {
        const newMap = new Map(prev);
        summaries.forEach(s => newMap.set(s.classId, s));
        return newMap;
      });
    } catch (err) {
      console.error('Error loading class summaries:', err);
    } finally {
      setLoadingSummaries(prev => {
        const newSet = new Set(prev);
        idsToLoad.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  }, [classSummaries, loadingSummaries]);

  // Load summaries when classes change
  useEffect(() => {
    if (classes.length > 0) {
      loadClassSummaries(classes.map(c => c.id));
    }
  }, [classes, loadClassSummaries]);

  // Helper to render count cell
  const renderCountCell = (classId: string, field: 'memberCount' | 'teacherCount') => {
    if (loadingSummaries.has(classId)) {
      return <Spinner size="tiny" />;
    }
    const summary = classSummaries.get(classId);
    if (!summary) return '-';
    const value = summary[field];
    if (value === -1) return <span className={styles.errorText}>Erro</span>;
    return value;
  };

  // Helper to render Teams status
  const renderTeamsCell = (classId: string, className: string) => {
    if (loadingSummaries.has(classId)) {
      return <Spinner size="tiny" />;
    }
    const summary = classSummaries.get(classId);
    if (!summary) return '-';

    // Already has Team
    if (summary.hasTeam) {
      return (
        <Tooltip content={summary.teamStatus === 'archived' ? 'Arquivado' : 'Ativo'} relationship="label">
          <Checkmark20Regular className={styles.teamsIcon} />
        </Tooltip>
      );
    }

    // Currently activating
    if (activatingTeams.has(classId)) {
      return (
        <Tooltip content="Ativando Teams..." relationship="label">
          <Spinner size="tiny" />
        </Tooltip>
      );
    }

    // Can activate (has at least 1 teacher)
    if (summary.teacherCount >= 1) {
      return (
        <Tooltip content="Clique para ativar o Teams" relationship="label">
          <Button
            appearance="subtle"
            icon={<Play20Regular className={styles.teamsIconActivate} />}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleActivateTeam(classId, className);
            }}
          />
        </Tooltip>
      );
    }

    // Cannot activate (no teachers)
    return (
      <Tooltip content="Sem Teams (necessario pelo menos 1 professor para ativar)" relationship="label">
        <Dismiss20Regular className={styles.teamsIconNone} />
      </Tooltip>
    );
  };

  // Helper to render Archived status
  const renderArchivedCell = (classId: string, className: string) => {
    if (loadingSummaries.has(classId) || unarchivingTeams.has(classId) || archivingTeams.has(classId)) {
      return <Spinner size="tiny" />;
    }
    const summary = classSummaries.get(classId);
    if (!summary) return '-';

    // No Team exists
    if (!summary.hasTeam) {
      return (
        <Tooltip content="Sem Teams" relationship="label">
          <span style={{ color: tokens.colorNeutralForeground4 }}>-</span>
        </Tooltip>
      );
    }

    // Team is archived
    if (summary.teamStatus === 'archived') {
      return (
        <Popover open={hoveredArchivedCell === classId}>
          <PopoverTrigger disableButtonEnhancement>
            <Archive20Regular
              className={styles.archivedIcon}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoveredArchivedCell(classId)}
              onMouseLeave={() => setHoveredArchivedCell(null)}
            />
          </PopoverTrigger>
          <PopoverSurface
            className={styles.archivedPopoverSurface}
            onMouseEnter={() => setHoveredArchivedCell(classId)}
            onMouseLeave={() => setHoveredArchivedCell(null)}
          >
            <Text size={200}>Sim - Grupo arquivado</Text>
            <Button
              appearance="primary"
              size="small"
              icon={<ArrowCounterclockwise20Regular />}
              onClick={() => {
                setHoveredArchivedCell(null);
                setUnarchiveTarget({ classId, className });
                setUnarchiveDialogOpen(true);
              }}
            >
              Ativar turma
            </Button>
          </PopoverSurface>
        </Popover>
      );
    }

    // Team is active (not archived)
    return (
      <Popover open={hoveredArchivedCell === classId}>
        <PopoverTrigger disableButtonEnhancement>
          <Subtract20Regular
            className={styles.archivedIconNotArchived}
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHoveredArchivedCell(classId)}
            onMouseLeave={() => setHoveredArchivedCell(null)}
          />
        </PopoverTrigger>
        <PopoverSurface
          className={styles.archivedPopoverSurface}
          onMouseEnter={() => setHoveredArchivedCell(classId)}
          onMouseLeave={() => setHoveredArchivedCell(null)}
        >
          <Text size={200}>Não - Grupo ativo</Text>
          <Button
            appearance="primary"
            size="small"
            icon={<Archive20Regular />}
            onClick={() => {
              setHoveredArchivedCell(null);
              setArchiveTarget({ classId, className });
              setArchiveDialogOpen(true);
            }}
          >
            Arquivar turma
          </Button>
        </PopoverSurface>
      </Popover>
    );
  };

  const getExportData = () => {
    return classes.map(cls => {
      const summary = classSummaries.get(cls.id);

      // Determine archived status
      let archivedStatus = '-';
      if (summary?.hasTeam) {
        archivedStatus = summary.teamStatus === 'archived' ? 'Sim' : 'Não';
      }

      return {
        displayName: cls.displayName,
        externalId: cls.externalId,
        id: cls.id,
        memberCount: summary?.memberCount ?? '',
        teacherCount: summary?.teacherCount ?? '',
        hasTeam: summary?.hasTeam ? 'Sim' : 'Nao',
        archived: archivedStatus,
      };
    });
  };

  const exportColumns = [
    { key: 'displayName', label: 'Nome' },
    { key: 'externalId', label: 'External ID' },
    { key: 'id', label: 'Entra ID' },
    { key: 'memberCount', label: 'Membros' },
    { key: 'teacherCount', label: 'Professores' },
    { key: 'hasTeam', label: 'Teams' },
    { key: 'archived', label: 'Arquivado' },
  ];

  // Calculate display range
  const startItem = classes.length > 0 ? 1 : 0;
  const endItem = classes.length;

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Carregando turmas..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {error && (
        <MessageBar intent="error" className={styles.warningBar}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* SEARCH AREA */}
      <div className={styles.searchArea}>
        <Dropdown
          className={styles.searchDropdown}
          value={searchBy === 'classCode' ? 'Código' : searchBy === 'name' ? 'Nome' : 'Entra ID'}
          selectedOptions={[searchBy]}
          onOptionSelect={(_, data) => setSearchBy(data.optionValue as SearchBy)}
        >
          <Option value="classCode">Código</Option>
          <Option value="name">Nome</Option>
          <Option value="id">Entra ID</Option>
        </Dropdown>

        <Input
          className={styles.searchInput}
          placeholder="Digite o termo de busca..."
          value={searchTerm}
          onChange={(_, data) => setSearchTerm(data.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
        />

        <Button
          appearance="primary"
          icon={<Search20Regular />}
          onClick={handleSearch}
        >
          Buscar
        </Button>

        {hasSearched && (
          <Button
            appearance="subtle"
            icon={<Dismiss20Regular />}
            onClick={handleClearSearch}
          >
            Limpar
          </Button>
        )}

        <ExportButton
          data={getExportData()}
          filename="turmas"
          columns={exportColumns}
        />

        {hasSearched && (
          <Text className={styles.totalText}>
            {classes.length} turma{classes.length !== 1 ? 's' : ''} carregada{classes.length !== 1 ? 's' : ''}
          </Text>
        )}
      </div>

      {/* Empty state */}
      {!hasSearched && (
        <div className={styles.emptyState}>
          <Text>Faça uma pesquisa para visualizar as turmas</Text>
        </div>
      )}

      {hasSearched && classes.length === 0 && !loading && (
        <div className={styles.emptyState}>
          <Text>Nenhuma turma encontrada</Text>
        </div>
      )}

      {/* TABLE AREA */}
      {hasSearched && classes.length > 0 && (
        <>
          <div className={styles.tableContainer}>
            <Table className={styles.table} size="small" aria-label="Classes table">
              <TableHeader className={styles.tableHeader}>
                <TableRow>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '50%' }}>
                    Nome
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '15%' }}>
                    ID Externo
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '6%' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <Tooltip content="Quantidade de alunos" relationship="label">
                        <People20Regular />
                      </Tooltip>
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '6%' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <Tooltip content="Quantidade de professores" relationship="label">
                        <PersonBoard20Regular />
                      </Tooltip>
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '7%' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <Tooltip content="Microsoft Teams" relationship="label">
                        <span>Teams</span>
                      </Tooltip>
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '8%' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <Tooltip content="Status de arquivamento do grupo" relationship="label">
                        <span>Arquivado</span>
                      </Tooltip>
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell className={styles.headerCell} style={{ width: '6%' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      Ação
                    </div>
                  </TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {classes.map((cls) => (
                  <TableRow key={cls.id} className={styles.tableRow}>
                    <TableCell style={{ width: '50%' }}>
                      <div className={styles.nameContent}>
                        <Class20Regular className={styles.classIcon} />
                        {cls.displayName}
                      </div>
                    </TableCell>
                    <TableCell style={{ width: '15%' }}>
                      {cls.externalId || '-'}
                    </TableCell>
                    <TableCell style={{ width: '6%' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        {renderCountCell(cls.id, 'memberCount')}
                      </div>
                    </TableCell>
                    <TableCell style={{ width: '6%' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        {renderCountCell(cls.id, 'teacherCount')}
                      </div>
                    </TableCell>
                    <TableCell style={{ width: '7%' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        {renderTeamsCell(cls.id, cls.displayName)}
                      </div>
                    </TableCell>
                    <TableCell style={{ width: '8%' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        {renderArchivedCell(cls.id, cls.displayName)}
                      </div>
                    </TableCell>
                    <TableCell style={{ width: '6%' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <Tooltip content="Ver detalhes" relationship="label">
                          <Button
                            appearance="subtle"
                            icon={<Eye20Regular />}
                            size="small"
                            onClick={() => handleViewClass(cls.id)}
                          />
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Loading more indicator */}
            {loadingMore && (
              <div className={styles.loadingMore}>
                <Spinner size="tiny" />
                <Text size={200}>Carregando mais...</Text>
              </div>
            )}
          </div>

          {/* PAGINATION AREA */}
          <div className={styles.paginationContainer}>
            <Text className={styles.paginationInfo}>
              Mostrando {startItem}-{endItem} turmas
            </Text>

            <div className={styles.paginationButtons}>
              {nextLink && (
                <Button
                  appearance="primary"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Carregando...' : 'Carregar mais'}
                </Button>
              )}
            </div>
          </div>
        </>
      )}

      <Dialog
        open={unarchiveDialogOpen}
        onOpenChange={(_, data) => {
          setUnarchiveDialogOpen(data.open);
          if (!data.open) setUnarchiveTarget(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Ativar turma arquivada</DialogTitle>
            <DialogContent>
              Deseja desarquivar o Microsoft Teams da turma{' '}
              <strong>"{unarchiveTarget?.className}"</strong>?
              <br /><br />
              Após esta ação, o Teams da turma voltará a ficar ativo.
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancelar</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleUnarchiveTeamConfirm}>
                Confirmar
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog
        open={archiveDialogOpen}
        onOpenChange={(_, data) => {
          setArchiveDialogOpen(data.open);
          if (!data.open) setArchiveTarget(null);
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Arquivar turma</DialogTitle>
            <DialogContent>
              Deseja arquivar o Microsoft Teams da turma{' '}
              <strong>"{archiveTarget?.className}"</strong>?
              <br /><br />
              Após esta ação, o Teams da turma ficará arquivado e inativo.
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancelar</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleArchiveTeamConfirm}>
                Confirmar
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <ClassDetails
        classId={selectedClassId}
        open={modalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default ClassesList;
